import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Empty, Link, Rule, Select, Stat, ui } from '../ui';
import { formatDate, formatMoney } from '@goodbuild/rules/format';
import type {
  AdminGiftRow,
  DirectoryCounts,
  DirectoryPerson,
  PeoplePage,
  PersonDetail,
} from '@api/views';
import { useApi } from '../api';
import { Field, Screen, Table } from './shared';
import { GiveAGame } from './admin-gifts';

/**
 * The directory: everybody on the platform, and the way to find any of them.
 *
 * Every other admin screen is a queue, and a queue only holds what has not been
 * decided yet — so the panel could reach a studio awaiting verification and not
 * the studio it verified last week, and an email address from a support ticket
 * was not a thing this product could look up at all.
 *
 * One search box rather than three. Users, studios and creators are not
 * separate populations — a creator is a user with a profile, a studio is a
 * company some users are members of — and splitting the search means knowing
 * which kind of thing you are looking for before you have found it, which is
 * backwards. Typing a studio's name here finds the people who act for it.
 *
 * Read-only, deliberately. Approving a creator and verifying a studio are
 * decisions that write several tables at once and live in the queues, which is
 * where they stay: a second way to make the same decision is a second thing to
 * keep atomic. What this adds is the ability to *find* the person first.
 */

/**
 * The core types, as they arrive over the wire.
 *
 * `JSON.parse` cannot give back a `Date`, so every timestamp on these shapes is
 * a string by the time this file sees it. Deriving that from core's own types
 * rather than restating them keeps one definition of what the endpoint returns
 * — and makes a field added there a typecheck failure here rather than a column
 * that silently never renders. It recurses, because the dates on this screen
 * are inside arrays: `PeoplePage.people[].joinedAt`, `recentOrders[].placedAt`.
 */
type Wire<T> = T extends Date
  ? string
  : T extends (infer Item)[]
    ? Wire<Item>[]
    : T extends object
      ? { [K in keyof T]: Wire<T[K]> }
      : T;

type PersonRow = Wire<DirectoryPerson>;

const ROLE_FILTERS = [
  { value: '', label: 'Everyone' },
  { value: 'player', label: 'Players only' },
  { value: 'creator', label: 'Creators' },
  { value: 'developer', label: 'Developers' },
  { value: 'admin', label: 'Administrators' },
] as const;

const PAGE = 50;

export function AdminPeopleRoute(): React.ReactElement {
  /*
   * Two pieces of state, not one.
   *
   * `draft` is what is in the box; `search` is what has been asked for. Firing
   * a query per keystroke would put a request on the wire for every prefix of
   * an email address, and every one of them runs five `like`s across the
   * directory. Submitting is also what makes the URL-less back button
   * predictable: the results on screen are the ones somebody asked for.
   */
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [offset, setOffset] = useState(0);

  const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
  if (search) params.set('q', search);
  if (role) params.set('role', role);

  const query = useApi<Wire<PeoplePage>>(
    ['admin', 'people', search, role, offset],
    `/admin/people?${params.toString()}`,
  );
  const counts = useApi<DirectoryCounts>(['admin', 'directory'], '/admin/directory');

  const page = query.data;
  const people = page?.people ?? [];
  const total = page?.total ?? 0;

  function ask(next: string): void {
    setSearch(next);
    // A new search that kept the old offset would open on page four of a
    // result set with three pages and look like it found nothing.
    setOffset(0);
  }

  return (
    <Screen title="People" aside={`${total} matching`} query={query}>
      {counts.data ? (
        <div className={ui.grid ?? ''}>
          <Stat value={String(counts.data.people)} label="Accounts" />
          {/*
            `Developers` and `Studios` stood beside this and went with the
            studios (#144). The role no longer exists and neither does the
            population it counted.
          */}
          <Stat value={String(counts.data.byRole.creator)} label="Creators" />
          <Stat
            value={`${counts.data.creators.total}`}
            label={`Creator profiles — ${counts.data.creators.awaitingApproval} waiting`}
          />
        </div>
      ) : null}

      <Rule />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          ask(draft.trim());
        }}
        className={ui.stack ?? ''}
      >
        <Field label="Find anyone">
          <input
            className={ui.input ?? ''}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Name, email, account id, creator handle or studio"
          />
        </Field>

        <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
          <Select
            options={ROLE_FILTERS}
            value={role}
            onChange={(next) => {
              setRole(next);
              setOffset(0);
            }}
            aria-label="Role"
          />

          <button type="submit" className={ui.button ?? ''}>
            Search
          </button>

          {search ? (
            <button
              type="button"
              className={ui.buttonSecondary ?? ''}
              onClick={() => {
                setDraft('');
                ask('');
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      </form>

      {people.length === 0 ? (
        <Empty>
          {search
            ? `Nothing matches “${search}”. An account id, an email address or part of a studio name all work here.`
            : 'No accounts yet. The first one appears when somebody signs up.'}
        </Empty>
      ) : (
        <>
          <Table head={['Person', 'Roles', 'Creator', 'Joined']}>
            {people.map((person) => (
              <tr key={person.id}>
                <td>
                  <Link href={`/admin/people/${person.id}`}>{person.name ?? 'No name'}</Link>
                  <br />
                  <span className={ui.statLabel ?? ''}>
                    {/*
                      An account with no identity row is a real state — deleted
                      at the provider, restored in part — and this screen exists
                      to show that it exists rather than to hide it.
                    */}
                    {person.email ?? 'No identity row'}
                    {person.email && !person.emailVerified ? ' — unverified' : ''}
                  </span>
                </td>
                <td>{describeRoles(person)}</td>
                <td>
                  {person.creator ? `@${person.creator.handle} — ${person.creator.state}` : '—'}
                </td>
                <td className={ui.numeric ?? ''}>{formatDate(new Date(person.joinedAt))}</td>
              </tr>
            ))}
          </Table>

          <Pager
            offset={offset}
            limit={PAGE}
            total={total}
            onMove={setOffset}
            showing={people.length}
          />
        </>
      )}
    </Screen>
  );
}

/**
 * Which roles somebody holds, and which they have only asked for.
 *
 * Section 9: Player is implicit on every account and is not stored, so it is
 * stated rather than listed from the data. An unapproved row is an application,
 * not a role, and saying "creator" for both would make the approval queue look
 * like it had already run.
 */
function describeRoles(person: Pick<PersonRow, 'roles'>): string {
  if (person.roles.length === 0) return 'Player';

  const held = person.roles.map((role) => (role.approvedAt ? role.role : `${role.role} (applied)`));
  return ['Player', ...held].join(', ');
}

function Pager({
  offset,
  limit,
  total,
  showing,
  onMove,
}: {
  offset: number;
  limit: number;
  total: number;
  showing: number;
  onMove: (next: number) => void;
}): React.ReactElement {
  const first = offset + 1;
  const last = offset + showing;

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--gb-space-4)',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <button
        type="button"
        className={ui.buttonSecondary ?? ''}
        disabled={offset === 0}
        onClick={() => onMove(Math.max(0, offset - limit))}
      >
        Previous
      </button>
      <span className={ui.statLabel ?? ''}>
        {first}–{last} of {total}
      </span>
      <button
        type="button"
        className={ui.buttonSecondary ?? ''}
        disabled={last >= total}
        onClick={() => onMove(offset + limit)}
      >
        Next
      </button>
    </div>
  );
}

/**
 * One account, in full.
 *
 * The question this answers is the support one: somebody has written in, and
 * what is needed is who they are, what they are allowed to do, what they have
 * bought and what they own. Every figure is summed on read — hard rule 2 — so
 * nothing here can be a stale total somebody then acts on.
 */
export function AdminPersonRoute(): React.ReactElement {
  const params = useParams({ strict: false }) as { userId?: string };
  const userId = params.userId ?? '';

  const query = useApi<{ person: Wire<PersonDetail> }>(
    ['admin', 'people', userId],
    `/admin/people/${encodeURIComponent(userId)}`,
  );
  const person = query.data?.person;
  const gifts = useApi<{ gifts: Wire<AdminGiftRow>[] }>(
    ['admin', 'gifts', userId],
    `/admin/gifts?user=${encodeURIComponent(userId)}`,
    { enabled: userId !== '' },
  );
  const givenToThem = gifts.data?.gifts ?? [];

  return (
    <Screen title={person?.name ?? 'Account'} aside={person?.email ?? userId} query={query}>
      {person ? (
        <>
          <div className={ui.grid ?? ''}>
            <Stat value={String(person.library.entitlements)} label="Games owned" />
            <Stat value={String(person.library.revoked)} label="Revoked" />
            <Stat
              value={
                person.spend.length === 0
                  ? '—'
                  : person.spend
                      .map((row) => formatMoney(row.currency as never, row.paidMinor))
                      .join(' · ')
              }
              label="Cleared spend"
            />
            <Stat value={formatDate(new Date(person.joinedAt))} label="Joined" />
          </div>

          <Rule />

          <Table head={['Fact', 'Value']}>
            <tr>
              <td>Account id</td>
              <td className={ui.numeric ?? ''}>{person.id}</td>
            </tr>
            <tr>
              <td>Email</td>
              <td>
                {person.email ? (
                  <>
                    <a href={`mailto:${person.email}`}>{person.email}</a>
                    {person.emailVerified ? ' — verified' : ' — not verified'}
                  </>
                ) : (
                  'No identity row for this account.'
                )}
              </td>
            </tr>
            <tr>
              <td>Roles</td>
              <td>{describeRoles(person)}</td>
            </tr>
            <tr>
              <td>First Hundred</td>
              {/*
                Section 5.5. One strike, across every title, permanently — and
                it bars future claims only. Saying so here stops it being read
                as a ban.
              */}
              <td>
                {person.firstHundredBarred
                  ? 'Barred from future claims. Nothing owned is affected.'
                  : 'Eligible'}
              </td>
            </tr>
          </Table>

          {/*
            A `Studios` table stood here, listing every organisation the account
            could act for. It went with the studios (#144).
          */}

          {person.creator ? (
            <>
              <h2 className={ui.sectionTitle ?? ''}>Creator</h2>
              <Table head={['Handle', 'Name', 'State', 'Level']}>
                <tr>
                  <td>
                    <Link href={`/@${person.creator.handle}`}>@{person.creator.handle}</Link>
                  </td>
                  <td>{person.creator.displayName}</td>
                  <td>{person.creator.state}</td>
                  <td className={ui.numeric ?? ''}>{person.creator.level}</td>
                </tr>
              </Table>
            </>
          ) : null}

          <h2 className={ui.sectionTitle ?? ''}>Give them a game</h2>
          <GiveAGame
            fixedRecipient={{
              id: person.id,
              label: person.name ?? person.email ?? person.id,
            }}
          />

          {givenToThem.length > 0 ? (
            <Table head={['Game', 'Why', 'Given', 'State']}>
              {givenToThem.map((gift) => (
                <tr key={gift.id}>
                  <td>{gift.productName}</td>
                  <td>
                    {gift.reason}
                    <br />
                    <span className={ui.statLabel ?? ''}>
                      by {gift.givenByName ?? gift.givenByUserId}
                    </span>
                  </td>
                  <td className={ui.numeric ?? ''}>{formatDate(new Date(gift.givenAt))}</td>
                  <td>
                    {gift.withdrawnAt === null
                      ? gift.recipientWasTold
                        ? 'Theirs, and told'
                        : 'Theirs, not told'
                      : 'Taken back'}
                  </td>
                </tr>
              ))}
            </Table>
          ) : null}

          <Rule />

          <h2 className={ui.sectionTitle ?? ''}>Orders</h2>
          {person.recentOrders.length === 0 ? (
            <Empty>Nothing bought yet.</Empty>
          ) : (
            <Table head={['Placed', 'State', 'Paid', 'Cleared']}>
              {person.recentOrders.map((row) => (
                <tr key={row.id}>
                  <td className={ui.numeric ?? ''}>{formatDate(new Date(row.placedAt))}</td>
                  <td>{row.state}</td>
                  <td className={ui.numeric ?? ''}>
                    {formatMoney(row.currency as never, row.pricePaidMinor)}
                  </td>
                  <td className={ui.numeric ?? ''}>
                    {row.clearedAt ? formatDate(new Date(row.clearedAt)) : '—'}
                  </td>
                </tr>
              ))}
            </Table>
          )}

          <Rule />
          <p className="gb-body">
            <Link href="/admin/people">Back to the directory</Link>
          </p>
        </>
      ) : null}
    </Screen>
  );
}
